import React, { useEffect, useRef, useState } from 'react'
import { Button, Card, Chip, Icon } from '@arnsbogroup/react-voicearchive'
import { useIntl } from 'react-intl'
import { useDropzone } from 'react-dropzone'
import messages from './messages'

const drawVisualizer = (analyser, canvasRef, animationRef) => {
  const canvas = canvasRef.current
  if (!canvas) return
  const ctx = canvas.getContext('2d')
  const bufferLength = analyser.frequencyBinCount
  const dataArray = new Uint8Array(bufferLength)

  canvas.width = 60
  canvas.height = 60

  const tempEl = document.createElement('div')
  tempEl.className = 'text-primary'
  document.body.appendChild(tempEl)
  const primaryColor = getComputedStyle(tempEl).color
  document.body.removeChild(tempEl)

  const draw = () => {
    animationRef.current = requestAnimationFrame(draw)
    analyser.getByteFrequencyData(dataArray)

    const { width, height } = canvas
    const cx = width / 2
    const cy = height / 2
    const baseRadius = width / 3

    ctx.clearRect(0, 0, width, height)

    ctx.beginPath()
    ctx.arc(cx, cy, baseRadius, 0, Math.PI * 2)
    ctx.strokeStyle = primaryColor
    ctx.lineWidth = 2
    ctx.stroke()

    for (let i = 0; i < bufferLength; i += 4) {
      let value = dataArray[i]
      value = Math.min(255, value * 1.8)
      const angle = (i / bufferLength) * 2 * Math.PI
      const barLength = 3 + (value / 255) * 10
      const x1 = cx + Math.cos(angle) * baseRadius
      const y1 = cy + Math.sin(angle) * baseRadius
      const x2 = cx + Math.cos(angle) * (baseRadius + barLength)
      const y2 = cy + Math.sin(angle) * (baseRadius + barLength)

      ctx.beginPath()
      ctx.moveTo(x1, y1)
      ctx.lineTo(x2, y2)
      ctx.strokeStyle = primaryColor.replace('rgb', 'rgba').replace(')', `, ${0.5 + (value / 255) * 0.5})`)
      ctx.lineWidth = 1.5
      ctx.stroke()
    }
  }

  draw()
}

const MemoryRecordCard = ({
  memory,
  isRecording,
  preview,
  submitting,
  analyserNode,
  onStartRecord,
  onStopRecord,
  onReRecord,
  onSubmitPreview,
  onUpload,
  onCancel = null,
  showUpload = true,
}) => {
  const { formatMessage } = useIntl()
  const audioRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const canvasRef = useRef(null)
  const animationRef = useRef(null)
  const [uploadMode, setUploadMode] = useState(showUpload)

  useEffect(() => {
    setUploadMode(showUpload)
  }, [showUpload])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => {
      if (files[0]) {
        setUploadMode(false)
        onUpload(files[0])
      }
    },
    accept: 'audio/*',
    multiple: false,
    disabled: submitting,
  })

  useEffect(() => {
    if (!preview) {
      setIsPlaying(false)
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.src = ''
      }
      return
    }
    if (audioRef.current) {
      audioRef.current.src = preview
      audioRef.current.load()
      setIsPlaying(false)
    }
  }, [preview])

  useEffect(() => {
    if (!isRecording || !analyserNode) {
      cancelAnimationFrame(animationRef.current)
      return
    }
    drawVisualizer(analyserNode, canvasRef, animationRef)
    return () => cancelAnimationFrame(animationRef.current)
  }, [isRecording, analyserNode])

  const togglePlay = () => {
    if (!audioRef.current) return
    if (isPlaying) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      setIsPlaying(false)
    } else {
      audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {})
    }
  }

  const enterUploadMode = () => {
    if (isRecording) onStopRecord()
    setUploadMode(true)
  }

  const exitUploadMode = () => {
    setUploadMode(false)
    onReRecord()
  }

  const isIdle = !uploadMode && !isRecording && !preview

  return (
    <Card className="p-3 space-y-2">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0 space-y-0.5">
          <p className="font-medium text-sm truncate">
            {memory.text || <span className="text-gray-400 italic">{formatMessage(messages.noText)}</span>}
          </p>
          {memory.language_name && <Chip size="xs">{memory.language_name}</Chip>}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {isIdle && (
            <Button
              size="sm"
              variant="outline"
              onClick={onStartRecord}
              disabled={submitting}
              icon={['fas', 'microphone']}
              iconPosition="left"
            >
              {formatMessage(messages.record)}
            </Button>
          )}

          {isRecording && (
            <button
              onClick={onStopRecord}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-red-100 text-red-600 hover:bg-red-200"
              title={formatMessage(messages.stopRecordingTitle)}
            >
              <Icon icon={['fas', 'stop']} />
            </button>
          )}

          {!isRecording && preview && (
            <>
              <button
                onClick={togglePlay}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
                title={isPlaying ? formatMessage(messages.stopTitle) : formatMessage(messages.playTitle)}
              >
                <Icon icon={['fas', isPlaying ? 'stop' : 'play']} />
              </button>
              <button
                onClick={onReRecord}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
                title={formatMessage(messages.reRecordTitle)}
              >
                <Icon icon={['fas', 'microphone']} />
              </button>
              <Button size="sm" state="primary" onClick={onSubmitPreview} disabled={submitting}>
                {submitting ? formatMessage(messages.submitting) : formatMessage(messages.submit)}
              </Button>
            </>
          )}

          {onCancel && (
            <button
              onClick={onCancel}
              className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
              title={formatMessage(messages.cancelTitle)}
            >
              <Icon icon={['fas', 'times']} />
            </button>
          )}
        </div>
      </div>

      {/* Recording: visualizer */}
      {isRecording && (
        <div className="flex items-center gap-3">
          <span className="text-xs text-red-500 flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            {formatMessage(messages.recording)}
          </span>
          <canvas ref={canvasRef} style={{ width: 60, height: 60 }} />
        </div>
      )}

      {/* Upload dropzone */}
      {uploadMode && (
        <div className="flex items-center gap-3 flex-wrap">
          <div
            {...getRootProps()}
            className={`px-3 py-2 border border-dashed rounded cursor-pointer text-sm transition-colors select-none ${
              isDragActive
                ? 'border-primary text-primary bg-primary/5'
                : 'border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700'
            } ${submitting ? 'opacity-50 pointer-events-none' : ''}`}
          >
            <input {...getInputProps()} />
            <span className="flex items-center gap-1.5">
              <Icon icon={['fas', 'upload']} />
              {submitting
                ? formatMessage(messages.uploading)
                : isDragActive
                ? formatMessage(messages.dropHere)
                : formatMessage(messages.upload)}
            </span>
          </div>
          <button
            onClick={exitUploadMode}
            className="text-xs text-gray-400 hover:text-gray-600 underline"
          >
            {formatMessage(messages.recordInstead)}
          </button>
        </div>
      )}

      {!uploadMode && !preview && showUpload && (
        <button
          onClick={enterUploadMode}
          className="text-xs text-gray-400 hover:text-gray-600 underline block"
        >
          {formatMessage(messages.cantRecord)}
        </button>
      )}

      <audio ref={audioRef} onEnded={() => setIsPlaying(false)} />
    </Card>
  )
}

export default MemoryRecordCard
